import fs from 'fs'

import type { ClusterName } from '../constants'
import { NotAProjectError } from '../errors'
import { clearAuth, loadAuth } from '../project/auth'
import { configPath, readConfig, writeConfig } from '../project/config'
import { clusterFor, normalizeServerUrl, resolveServerTarget } from './server-target'
import type { OperationOptions } from './types'

// Pointing a project at a different Flowrunner cluster.
//
// The reason this is a command rather than "edit flowrunner.json" is the login. A deploy token is issued by
// one server and a workspace id only exists on that server, so a project carrying both across a switch is
// carrying credentials that cannot work — and the failure would arrive later, as a 401 in the middle of a
// deploy, rather than here where the cause is obvious. Switching therefore detaches both.

export { CLUSTER_NAMES, clusterFor, normalizeServerUrl, resolveServerTarget } from './server-target'

export interface UseServerResult {
  /** The cluster switched to, or null for a URL that is not one of ours. */
  cluster: ClusterName | null
  serverUrl: string
  previousServerUrl: string
  /** False when the project was already pointed at this server — nothing was touched. */
  changed: boolean
  /** A login existed for the previous server and was detached. */
  clearedLogin: boolean
  /** The workspace that was detached, if there was one. */
  clearedWorkspaceId: string
}

/** Point the project at `target`, detaching any login that belonged to the server it was on. */
export function useServer(target: string, options: OperationOptions = {}): UseServerResult {
  const projectRoot = options.cwd ?? process.cwd()

  if (!fs.existsSync(configPath(projectRoot))) {
    throw new NotAProjectError()
  }

  const serverUrl = resolveServerTarget(target)
  const config = readConfig(projectRoot)
  const previousServerUrl = normalizeServerUrl(config.serverUrl ?? '')

  // Switching to the server you are already on must not log you out — that would make an idempotent
  // command destructive, and `use dev` is exactly the thing someone types to check where they are.
  if (previousServerUrl === serverUrl) {
    return {
      cluster           : clusterFor(serverUrl),
      serverUrl,
      previousServerUrl,
      changed           : false,
      clearedLogin      : false,
      clearedWorkspaceId: '',
    }
  }

  const clearedLogin = Boolean(loadAuth(projectRoot))
  const clearedWorkspaceId = config.workspaceId ?? ''

  // The workspace and the name go with the old server; only `serverUrl` survives the switch.
  const { workspaceId: _workspaceId, workspaceName: _workspaceName, ...rest } = config

  writeConfig({ ...rest, serverUrl }, projectRoot)
  clearAuth(projectRoot)

  return {
    cluster: clusterFor(serverUrl),
    serverUrl,
    previousServerUrl,
    changed: true,
    clearedLogin,
    clearedWorkspaceId,
  }
}
